import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import PageChrome from "../../../../components/page-chrome";
import {
  Badge,
  Button,
  Card,
  Column,
  DataPoint,
  DropdownButton,
  Icon,
  Row,
  Text,
  Tooltip,
  useDesignTokens,
} from "@gradience/ui";
import {
  getQueryOptions,
  queryKeys,
  useApiPost,
  useApiQuery,
} from "../../../../lib/api";
import { IconContainer, IconName } from "@gradience/ui/dist/components/icon";
import {
  useLatestTest,
  useSelectedTest,
} from "../../../../domain/use-latest-test";
import { useHover } from "@uidotdev/usehooks";
import AddStudentsModal from "../add-students-modal";
import EditGroupModal from "../edit-group-modal";
import { ReactNode, useMemo, useState } from "react";
import EditStudentModal from "../edit-student-modal";
import { isDatePast } from "../../../../lib/date-utils";
import toast from "../../../../domain/toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import stripTimeZone from "../../../../lib/strip-time-zone";
import useNumberOfQuestions from "../../../../domain/use-number-of-questions";
import { isGroupTestAvailable } from "../../../../domain/is-group-test-available";
import { DateTime } from "luxon";
import UploadTestsModal from "../upload-tests-modal";
import { getGroupQueryOptions } from "./utils";
import { StudentsTable } from "./students-table";
import { KeyConceptsTable } from "./key-concepts-table";

const groupModals = [
  "add-students",
  "edit",
  "edit-student",
  "upload-tests",
] as const;
type GroupRouteSearch = {
  groupModal?: (typeof groupModals)[number];
  editingStudentId?: string;
  preview?: boolean;
};

export const Route = createFileRoute("/$test-slug/groups/$id/")({
  component: GroupPage,
  validateSearch: (search): GroupRouteSearch => {
    const validatedSearch: GroupRouteSearch = {};
    if (groupModals.includes(search.groupModal as any)) {
      validatedSearch.groupModal = search.groupModal as any;
    }
    if (
      search.editingStudentId &&
      typeof search.editingStudentId === "string"
    ) {
      validatedSearch.editingStudentId = search.editingStudentId;
    }
    if (search.preview === true) {
      validatedSearch.preview = true;
    }

    return validatedSearch;
  },
});

function GroupPage() {
  const params = useParams({ from: "/$test-slug/groups/$id/" });
  const search = useSearch({ from: "/$test-slug/groups/$id/" });
  const selectedTest = useSelectedTest();
  const groupQuery = useQuery({
    ...getGroupQueryOptions({
      testId: selectedTest.data?.id,
      groupId: params.id,
    }),
    refetchInterval: (result) => {
      const group = result?.state?.data?.data.find(
        (group) => group.id === params.id
      );
      return group?.isGeneratingStudyGuide ? 1000 : 0;
    },
  });
  const designTokens = useDesignTokens();
  const navigate = useNavigate({ from: "/$test-slug/groups/$id" });
  const groupDetail = useApiQuery("/groups/:id", { id: params.id });
  const test = useSelectedTest();
  const latestTest = useLatestTest();

  const loading = groupQuery.isLoading || test.isLoading;

  const students = useQuery(
    getQueryOptions({
      endpoint: "/students",
      queryParams: { groupId: params.id },
    })
  );

  const newGroups = useApiQuery(
    "/groups",
    {},
    {},
    { testId: latestTest.data?.id }
  );
  const [copiedRoster, setCopiedRoster] = useState<string>();
  const createStudentsMutation = useApiPost("/student-lists");

  const queryClient = useQueryClient();

  const configurationAvailable =
    test.isLoading ||
    !test.data ||
    (isDatePast(test.data.configurationDateStart) &&
      !isDatePast(test.data.configurationDateEnd));

  const configurationPast = isDatePast(test.data?.configurationDateEnd);

  const numberOfQuestions = useNumberOfQuestions(
    groupQuery.data?.conceptIds ?? [],
    test.data?.id
  );

  const testScans = useApiQuery("/groups/:id/test-scans", {
    id: params.id,
  });

  const administrationAvailable = useMemo(() => {
    if (!groupQuery.data?.testDate) {
      return false;
    }

    const testDate = new Date(groupQuery.data.testDate);
    testDate.setDate(testDate.getDate() - 5); // Subtract 5 days from test date

    return new Date() >= testDate;
  }, [groupQuery.data?.testDate]);

  const groupIsConfigured = (groupQuery.data?.conceptIds.length ?? 0) > 0;
  const isReviewActive =
    isDatePast(test.data?.reviewDateStart) &&
    !isDatePast(test.data?.reviewDateEnd);

  return (
    <PageChrome loading={loading}>
      <AddStudentsModal
        onSubmit={() => {
          // numberOfStudents will be updated
          groupQuery.refetch();
        }}
      />
      <UploadTestsModal
        onComplete={() =>
          navigate({ to: "/$test-slug/groups/$id/inspect-scans" })
        }
        groupId={params["id"]}
        isOpen={search.groupModal === "upload-tests"}
        onClose={() => {
          navigate({ search: (prev) => ({ ...prev, groupModal: undefined }) });
        }}
      />
      <EditGroupModal />
      <EditStudentModal />
      {test.data ? (
        <Column style={{ gap: 40, flex: 1 }}>
          <Row
            gap={16}
            style={{
              alignItems: "center",
            }}
          >
            <Row
              gap={8}
              style={{
                alignItems: "flex-start",
                flex: 1,
              }}
            >
              <Icon
                name="arrow-left"
                to={`/${test.data?.yearIdentifier}/home`}
              />
              <Column>
                <Text textStyle="32px - Bold">{groupQuery.data?.name}</Text>
                <Text textStyle="14px - Semibold">
                  Instructor {groupQuery.data?.instructorFirstName}{" "}
                  {groupQuery.data?.instructorLastName}
                </Text>
              </Column>
            </Row>
            {isDatePast(test.data?.completionDate) ? (
              <DropdownButton
                text={
                  copiedRoster
                    ? `Copied to ${copiedRoster}`
                    : `Copy Roster to ${latestTest.data?.yearIdentifier} Group`
                }
                onSelect={async (groupId) => {
                  const group = newGroups.data?.data.find(
                    (group) => group.id === groupId
                  );
                  if (!group) {
                    return;
                  }
                  await createStudentsMutation.mutateAsync({
                    body: {
                      groupId: group.id,
                      students:
                        students.data?.data.map((student) => ({
                          firstName: student.firstName,
                          lastName: student.lastName,
                          grade: student.grade,
                        })) ?? [],
                    },
                  });
                  setCopiedRoster(group.name);
                  queryClient.invalidateQueries({
                    queryKey: queryKeys["/groups/:id"],
                  });
                  toast({
                    message: "Roster copied successfully.",
                    icon: "Students",
                    action: {
                      label: "Go To Group",
                      onClick: () => {
                        navigate({
                          to: "/$test-slug/groups/$id",
                          params: {
                            "test-slug": latestTest.data?.yearIdentifier,
                            id: groupId,
                          },
                        });
                      },
                    },
                  });
                }}
                variant="subdued"
                size="small"
                icon={copiedRoster ? "check-02" : undefined}
                disabled={copiedRoster !== undefined}
                loading={createStudentsMutation.isPending}
                options={
                  newGroups.data?.data.map((group) => ({
                    label: group.name,
                    value: group.id,
                  })) ?? []
                }
              />
            ) : (
              <Button
                text="Edit Info"
                variant="subdued"
                size="small"
                to="?groupModal=edit"
              />
            )}
          </Row>
          <Column gap={20}>
            <Row gap={8}>
              <DataPoint
                icon="calendar-check"
                title={
                  groupQuery.data?.testDate
                    ? stripTimeZone(
                        new Date(groupQuery.data.testDate)
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })
                    : "—"
                }
                subtitle="Exam Date"
              />
              <DataPoint
                icon="users-profile-03"
                title={groupQuery.data?.numberOfStudents?.toString() ?? ""}
                subtitle="Students"
              />
              <DataPoint
                icon="file-branch"
                title={groupQuery.data?.conceptIds?.length.toString() ?? ""}
                subtitle="Concepts Tested"
              />
              <DataPoint
                icon="file-question-02"
                title={
                  numberOfQuestions.data?.numberOfQuestions.toString() ?? ""
                }
                subtitle="Exam Questions"
              />
            </Row>
            {isDatePast(test.data.completionDate) || search.preview ? <KeyConceptsTable /> : null}
            <StudentsTable preview={search.preview} />
            {latestTest.data?.id === test.data?.id ? (
              <Row gap={20}>
                <Card
                  style={{
                    gap: 24,
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      backgroundColor: configurationAvailable
                        ? designTokens.colors.misc.Brand
                        : designTokens.colors.surface.Mid,
                    }}
                  />
                  <Row
                    gap={16}
                    style={{
                      alignItems: "center",
                      opacity: configurationAvailable ? 1 : 0.5,
                    }}
                  >
                    <IconContainer icon="file-edit-02" />
                    <Text textStyle="20px - Bold" style={{ flex: 1 }}>
                      Set Up
                    </Text>
                    <Badge
                      type={configurationPast ? "info" : "primary"}
                      icon="calendar-07"
                    >
                      {configurationPast
                        ? `Ended ${
                            test.data
                              ? new Date(
                                  test.data.configurationDateEnd
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : ""
                          }`
                        : `Now – ${
                            test.data
                              ? new Date(
                                  test.data.configurationDateEnd
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : ""
                          }`}
                    </Badge>
                  </Row>
                  <Column
                    style={{
                      opacity: configurationAvailable ? 1 : 0.5,
                    }}
                  >
                    <ActionLink
                      title="Add Students"
                      icon="plus-01"
                      to="?groupModal=add-students"
                      isComplete={(groupQuery.data?.numberOfStudents ?? 0) > 0}
                      badgeContent={
                        (groupQuery.data?.numberOfStudents ?? 0) > 0
                          ? "Added"
                          : `Before ${
                              test.data
                                ? new Date(
                                    test.data.configurationDateEnd
                                  ).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })
                                : ""
                            }`
                      }
                    />
                    <ActionLink
                      title="Configure Tests"
                      icon="arrow-right"
                      to={`/${test.data?.yearIdentifier}/groups/${params.id}/configure`}
                      disabled={!configurationAvailable}
                      isComplete={groupIsConfigured}
                      badgeContent={
                        groupIsConfigured
                          ? "Configured"
                          : configurationAvailable
                            ? `Before ${DateTime.fromISO(test.data.configurationDateStart).toFormat("MMM d")}`
                            : configurationPast
                              ? "Configuration Closed"
                              : `Available ${
                                  test.data
                                    ? new Date(
                                        test.data.configurationDateStart
                                      ).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                      })
                                    : ""
                                }`
                      }
                    />
                    <ActionLink
                      newTab
                      to={groupQuery.data?.studyGuideUrl}
                      title="Download Study Guide"
                      icon="download-02"
                      disabled={!groupQuery.data?.studyGuideUrl}
                      badgeContent={
                        groupQuery.data && groupQuery.data.conceptIds.length > 0
                          ? groupQuery.data.isGeneratingStudyGuide
                            ? "Generating..."
                            : ""
                          : "Available after configuration"
                      }
                      finalElement
                    />
                  </Column>
                </Card>
                <Card
                  style={{
                    flex: 1,
                  }}
                >
                  <Column
                    style={{
                      gap: 24,
                      flex: 1,
                    }}
                  >
                    <span
                      style={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: administrationAvailable
                          ? designTokens.colors.misc.Brand
                          : designTokens.colors.surface.Mid,
                      }}
                    />
                    <Row
                      gap={16}
                      style={{
                        alignItems: "center",
                        opacity: administrationAvailable ? 1 : 0.5,
                      }}
                    >
                      <IconContainer icon="file-check-02" />
                      <Text textStyle="20px - Bold" style={{ flex: 1 }}>
                        Administer & Review
                      </Text>
                      <Badge
                        icon="calendar-07"
                        type={administrationAvailable ? "primary" : "info"}
                      >
                        {`${DateTime.fromISO(test.data.printDateStart).toFormat(
                          "MMM d"
                        )} – ${DateTime.fromISO(
                          test.data.administrationDateEnd
                        ).toFormat("MMM d")}`}
                      </Badge>
                    </Row>
                    <Column
                      style={{
                        opacity: administrationAvailable ? 1 : 0.5,
                      }}
                    >
                      <ActionLink
                        title="Print Tests"
                        icon="print 1"
                        newTab
                        disabled={!groupQuery.data?.testUrl}
                        to={groupQuery.data?.testUrl}
                        badgeContent={
                          groupQuery.data &&
                          test.data &&
                          isGroupTestAvailable({ group: groupQuery.data }) &&
                          groupQuery.data.testDate
                            ? groupQuery.data.testUrl
                              ? `Between ${DateTime.fromISO(
                                  groupQuery.data.testDate
                                )
                                  .minus({
                                    days: 5,
                                  })
                                  .toFormat("MMM d")} – ${DateTime.fromISO(
                                  groupQuery.data.testDate
                                ).toFormat("MMM d")}`
                              : "Generating..."
                            : groupQuery.data?.testDate
                              ? `Between ${DateTime.fromISO(
                                  groupQuery.data.testDate
                                )
                                  .minus({
                                    days: 5,
                                  })
                                  .toFormat("MMM d")} – ${DateTime.fromISO(
                                  groupQuery.data.testDate
                                ).toFormat("MMM d")}`
                              : undefined
                        }
                      />
                      <ActionLink
                        title="Upload Tests"
                        icon="upload-03"
                        to={
                          groupDetail.data?.uploadWarning
                            ? `/${test.data.yearIdentifier}/groups/${params.id}/inspect-scans`
                            : groupDetail.data?.uploadSuccess
                              ? undefined
                              : "?groupModal=upload-tests"
                        }
                        disabled={
                          !groupQuery.data?.testUrl || testScans.isPending
                        }
                        badgeContent={
                          groupDetail.data?.uploadWarning ? (
                            <Tooltip
                              content="Your upload has some issues that need to be
                                  resolved before your test can be graded. Press
                                  here to inspect your upload."
                            >
                              <Badge
                                type="warning"
                                icon="information-circle-contained"
                              >
                                Warning
                              </Badge>
                            </Tooltip>
                          ) : groupDetail.data?.uploadSuccess ? (
                            <Badge type="success" icon="check-01">
                              Uploaded
                            </Badge>
                          ) : testScans.data?.data.some(
                              (scan) => scan.fileError
                            ) ? (
                            <Badge
                              type="warning"
                              icon="information-circle-contained"
                            >
                              Upload Error
                            </Badge>
                          ) : groupDetail.data?.uploadProcessing ? (
                            <Badge type="info" icon="arrow-refresh-04">
                              Processing
                            </Badge>
                          ) : undefined
                        }
                      />
                      <ActionLink
                        title="Review Test Grading"
                        icon="arrow-right"
                        to={`/${test.data.yearIdentifier}/groups/${params.id}/review`}
                        disabled={!groupQuery.data?.isGraded || !isReviewActive}
                        finalElement
                        badgeContent={
                          groupQuery.data?.isReviewed ? (
                            <Badge type="success" icon="check-01">
                              Reviewed
                            </Badge>
                          ) : groupQuery.data?.isGraded && isReviewActive ? (
                            <Badge type="primary">Review Tests</Badge>
                          ) : null
                        }
                      />
                    </Column>
                  </Column>
                </Card>
              </Row>
            ) : null}
          </Column>
        </Column>
      ) : null}
    </PageChrome>
  );
}

function ActionLink({
  title,
  badgeContent,
  icon,
  disabled,
  to,
  finalElement,
  newTab,
  isComplete,
}: {
  title: string;
  badgeContent?: string | ReactNode;
  icon: IconName;
  disabled?: boolean;
  to?: string;
  finalElement?: boolean;
  newTab?: boolean;
  isComplete?: boolean;
}) {
  const [ref, hovered] = useHover();
  const designTokens = useDesignTokens();

  return (
    <Link
      ref={ref}
      to={to}
      disabled={disabled}
      rel={newTab ? "noreferrer" : undefined}
      style={{
        padding: "20px 16px",
        borderBottom: finalElement ? "none" : "1px solid #E3E3E3",
        alignItems: "center",
        display: "flex",
        flexDirection: "row",
        textDecoration: "none",
        gap: 16,
      }}
    >
      <Row gap={8} style={{ flex: 1, alignItems: "center" }}>
        <Text textStyle="18px - Bold">{title}</Text>
        {badgeContent &&
          (typeof badgeContent === "string" ? (
            <Badge
              type={isComplete ? "success" : disabled ? "info" : "primary"}
              icon={isComplete ? "check-01" : undefined}
            >
              {badgeContent}
            </Badge>
          ) : (
            badgeContent
          ))}
      </Row>
      <Icon
        name={icon}
        onClick={() => {}}
        disabled={disabled}
        style={{
          backgroundColor:
            hovered && !disabled
              ? designTokens.colors.surface.Disabled
              : "transparent",
        }}
      />
    </Link>
  );
}
